import { MW_CATEGORIES, MW_IR_INTENSITIES, type MwStage } from '@badger-sentinel/rules';

import { defaultOpFor, type FilterKind, type FilterPredicate, type FilterValue } from './filter.js';
import { observationFor, stageOfField, type MwModelWire, type MwTrackWire } from './mw.js';

/**
 * RB §5.3.5's recognition table (design 996–1135).
 *
 * A **static reference document**. It does not know which track is selected and
 * it never reacts to one: the player reads it and draws the conclusion, and the
 * console does not draw it for them. The one concession is the pinned
 * observed-signature strip, which puts *this* track's readings on the same
 * columns — a comparison the operator would otherwise make by memory, under a
 * clock, between two panels.
 *
 * ## Prose and numbers ride together (F3)
 *
 * `apogee` is the rulebook's own sentence and `apogeeKm` is the band the filter
 * compares. Both are on the wire, deliberately: sending only the prose would make
 * "apogee between 300 and 600" a string match, and sending only the numbers would
 * replace the rulebook's table with a spreadsheet. The APOGEE column therefore
 * *displays* the band and carries the prose underneath it.
 *
 * ## `apogeeKm: null` is a row with no ballistic apogee
 *
 * Four of the eleven rows have one — every space launch, the direct-ascent ASAT
 * and the test shot. It is not a zero. `matchesBand` refuses every numeric
 * comparison on a null band, so a filter for `APOGEE < 200` excludes them rather
 * than sweeping them all in, and the cell says `NO BALLISTIC APOGEE` rather than
 * a dash that would read as missing data.
 */

export type RecognitionColumnKey =
  | 'type'
  | 'category'
  | 'irIntensity'
  | 'boostDurationS'
  | 'stagingEvents'
  | 'apogeeKm'
  | 'groundTrack'
  | 'terminal'
  | 'rv';

export interface RecognitionColumn {
  readonly key: RecognitionColumnKey;
  readonly label: string;
  /** `null` for the TYPE column, which names the row rather than describing it. */
  readonly kind: FilterKind | null;
  readonly unit: string | null;
  readonly width: number;
  /**
   * **The stage that reveals this observable**, and therefore the order the
   * table is published in.
   *
   * Not a taste ranking: `MW_OBSERVABLE_FIELDS` says infrared arrives at stage 1
   * and terminal behaviour at stage 4, so a reader scanning left to right walks
   * the reveal in the order it actually arrives. TYPE is `0` and names the row.
   *
   * It used to decide which columns were *dropped* at a given width as well.
   * That is gone — see the note below `FILTERABLE_COLUMNS`.
   */
  readonly priority: 0 | MwStage;
  /** The observation field this column compares against, where there is one. */
  readonly observes: string | null;
}

export const RECOGNITION_COLUMNS: readonly RecognitionColumn[] = Object.freeze([
  { key: 'type', label: 'TYPE', kind: null, unit: null, width: 180, priority: 0, observes: null },
  {
    key: 'category',
    label: 'CATEGORY',
    kind: 'cat',
    unit: null,
    width: 132,
    // Not an observable at all — it is the taxonomy a misclassification is priced
    // against (RB §5.3.6: same category −2, different category −4), so it is the
    // last column an operator should lose.
    priority: 1,
    observes: null,
  },
  {
    key: 'irIntensity',
    label: 'IR',
    kind: 'cat',
    unit: null,
    width: 88,
    priority: 1,
    observes: 'irIntensity',
  },
  {
    key: 'boostDurationS',
    label: 'BOOST',
    kind: 'num',
    unit: 'S',
    width: 104,
    priority: 2,
    observes: 'boostDurationS',
  },
  {
    key: 'stagingEvents',
    label: 'STAGING',
    kind: 'num',
    unit: null,
    width: 88,
    priority: 2,
    observes: 'stagingEvents',
  },
  {
    key: 'apogeeKm',
    label: 'APOGEE',
    kind: 'num',
    unit: 'KM',
    width: 116,
    priority: 3,
    observes: 'apogee',
  },
  {
    key: 'groundTrack',
    label: 'GROUND TRACK',
    kind: 'text',
    unit: null,
    width: 168,
    priority: 3,
    observes: 'groundTrack',
  },
  {
    key: 'terminal',
    label: 'TERMINAL',
    kind: 'text',
    unit: null,
    width: 196,
    priority: 4,
    observes: 'terminal',
  },
  { key: 'rv', label: 'RV', kind: 'num', unit: null, width: 64, priority: 4, observes: 'rv' },
] as const);

export function columnByKey(key: string): RecognitionColumn | undefined {
  return RECOGNITION_COLUMNS.find((column) => column.key === key);
}

/** The columns an operator may write a predicate against — everything but TYPE. */
export const FILTERABLE_COLUMNS: readonly RecognitionColumn[] = RECOGNITION_COLUMNS.filter(
  (column): column is RecognitionColumn & { kind: FilterKind } => column.kind !== null,
);

/*
 * There was a `columnsFor(availableWidth)` here, which took whole priority tiers
 * and dropped the rest. It is gone, and the reason is worth keeping.
 *
 * `priority` is the stage that reveals the observable, so dropping from the back
 * dropped **the last thing the operator learns first**. At the committed 1280
 * floor with both rails open the table had 608 px and showed five of nine
 * columns — stage 3 and stage 4 withheld — so the reference document lost its
 * discriminators at exactly the moment the operator had the most data and the
 * least time. Worse, `observedSignature` is computed against the same list, so
 * the seat's *own* stage-3 and stage-4 readings vanished from the pinned strip
 * with them. Nothing on screen said any of it, and the documented recovery —
 * collapse a rail — only worked for the right rail, the one holding RELEASE.
 *
 * The table now renders every column and scrolls, with TYPE frozen at the left
 * so the row label is never the thing that scrolls away. That was the tier
 * system's one real insight and it is kept: a table with no row labels is not a
 * narrower table, it is a different document.
 */

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

function bandText(band: readonly [number, number], unit: string | null): string {
  const [lo, hi] = band;
  const body = lo === hi ? String(lo) : String(lo) + '–' + String(hi);
  return unit === null ? body : body + ' ' + unit;
}

/** `NO BALLISTIC APOGEE`, and not a dash: the absence is the discriminator. */
export const NO_BAND = 'NO BALLISTIC APOGEE';

export interface RecognitionCell {
  readonly key: RecognitionColumnKey;
  readonly text: string;
  /** The rulebook's own sentence, where the column shows a band instead. */
  readonly prose: string | null;
}

export function cellFor(model: MwModelWire, column: RecognitionColumn): RecognitionCell {
  switch (column.key) {
    case 'type':
      return { key: column.key, text: model.displayName, prose: model.id };
    case 'category':
      return { key: column.key, text: model.category, prose: null };
    case 'irIntensity':
      return { key: column.key, text: model.irIntensity, prose: null };
    case 'boostDurationS':
      return { key: column.key, text: bandText(model.boostDurationS, column.unit), prose: null };
    case 'stagingEvents':
      return { key: column.key, text: String(model.stagingEvents), prose: null };
    case 'apogeeKm':
      return {
        key: column.key,
        text: model.apogeeKm === null ? NO_BAND : bandText(model.apogeeKm, column.unit),
        prose: model.apogee,
      };
    case 'groundTrack':
      return { key: column.key, text: model.groundTrack, prose: null };
    case 'terminal':
      return { key: column.key, text: model.terminal, prose: null };
    case 'rv':
      return { key: column.key, text: bandText(model.rv, null), prose: null };
  }
}

/** What one catalogue row offers one filter column. */
export function valueOf(model: MwModelWire, columnKey: string): FilterValue | null {
  const column = columnByKey(columnKey);
  if (column === undefined || column.kind === null) return null;
  switch (column.key) {
    case 'category':
      return { kind: 'cat', text: model.category };
    case 'irIntensity':
      return { kind: 'cat', text: model.irIntensity };
    case 'boostDurationS':
      return { kind: 'num', band: model.boostDurationS };
    case 'stagingEvents':
      return { kind: 'num', band: [model.stagingEvents, model.stagingEvents] };
    case 'apogeeKm':
      // The one nullable band, and the reason `matchesBand` refuses rather than
      // substitutes: a launch with no ballistic apogee satisfies no apogee test.
      return { kind: 'num', band: model.apogeeKm };
    case 'groundTrack':
      return { kind: 'text', text: model.groundTrack };
    case 'terminal':
      return { kind: 'text', text: model.terminal };
    case 'rv':
      return { kind: 'num', band: model.rv };
    default:
      return null;
  }
}

/** The values a categorical column can take, from the shared enums. */
export function categoryOptionsFor(column: RecognitionColumn): readonly string[] {
  if (column.key === 'category') return MW_CATEGORIES;
  if (column.key === 'irIntensity') return MW_IR_INTENSITIES;
  return [];
}

// ---------------------------------------------------------------------------
// The pinned observed-signature strip
// ---------------------------------------------------------------------------

export interface ObservedCell {
  readonly key: RecognitionColumnKey;
  /** The reading, or null in both absent cases — `state` says which. */
  readonly text: string | null;
  readonly state: 'OBSERVED' | 'DROPPED' | 'PENDING' | 'NONE';
  /** A number this reading can seed a filter with, where one can be read out of it. */
  readonly seed: string | null;
  /** The stage this column's reading is due at, for the PENDING case. */
  readonly dueAtStage: MwStage | null;
}

/**
 * The first number in a reading, or null.
 *
 * The wire sends `420–520 s`, `2` and `Rises continuously to orbital altitude` —
 * all three are legitimate readings of a column the filter compares numerically,
 * and only the first two contain a number. A cell that cannot seed a filter is
 * not a button, which is the honest affordance: the apogee this track shows is
 * prose, and prose does not go in a numeric comparison.
 */
export function seedValueOf(text: string): string | null {
  const match = /-?\d+(?:\.\d+)?/.exec(text);
  return match === null ? null : match[0];
}

/**
 * This track's readings, on the table's own columns.
 *
 * Four states, and the two absent ones are **not** the same absence — the same
 * rule the observation rows follow, carried onto the strip so the operator
 * comparing the table against their track sees which columns they are still owed
 * and which ones they will never get.
 */
export function observedSignature(
  track: MwTrackWire,
  columns: readonly RecognitionColumn[],
): readonly ObservedCell[] {
  return columns
    .filter((column) => column.key !== 'type')
    .map((column) => {
      const field = column.observes;
      if (field === null) {
        return {
          key: column.key,
          text: null,
          state: 'NONE' as const,
          seed: null,
          dueAtStage: null,
        };
      }
      const due = stageOfField(field);
      const observation = observationFor(track, field);
      if (observation === undefined) {
        return {
          key: column.key,
          text: null,
          state: 'PENDING' as const,
          seed: null,
          dueAtStage: due,
        };
      }
      if (observation.value === null) {
        return {
          key: column.key,
          text: null,
          state: 'DROPPED' as const,
          seed: null,
          dueAtStage: due,
        };
      }
      return {
        key: column.key,
        text: observation.value,
        state: 'OBSERVED' as const,
        seed: column.kind === 'num' ? seedValueOf(observation.value) : null,
        dueAtStage: due,
      };
    });
}

// ---------------------------------------------------------------------------
// Predicate rows
// ---------------------------------------------------------------------------

let predicateSeq = 0;

export function newPredicate(column?: RecognitionColumn): FilterPredicate {
  const target = column ?? FILTERABLE_COLUMNS[0];
  const kind: FilterKind = target?.kind ?? 'num';
  predicateSeq += 1;
  return {
    id: 'F' + String(predicateSeq),
    column: target?.key ?? '',
    op: defaultOpFor(kind),
    a: '',
    b: '',
    on: true,
  };
}

/** Test seam, so predicate ids are stable across a suite. */
export function resetPredicateKeys(): void {
  predicateSeq = 0;
}

/** How one armed predicate reads back as a sentence, for the empty state's chips. */
export function predicateText(predicate: FilterPredicate): string {
  const column = columnByKey(predicate.column);
  if (column === undefined) return predicate.column;
  const unit = column.kind === 'num' && column.unit !== null ? ' ' + column.unit : '';
  if (predicate.op === 'BETWEEN') {
    return column.label + ' ' + predicate.a + '–' + predicate.b + unit;
  }
  return column.label + ' ' + predicate.op + ' ' + predicate.a + unit;
}
